import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ConfirmDeleteModal from "@shared/components/ConfirmationModal";
import TemplateCard from "@pages/Template/components/TemplateCard";
import TemplatesTabs from "@pages/Template/components/TemplatesTabs";
import useFetchTemplates from "@pages/Template/utils/useFetchTemplates";
import useDeleteTemplate from "@pages/Template/utils/useHandleDelete";
import useGenerateReport from "@pages/Template/utils/useGenerateReport";
import SubTitleHeader from "@shared/components/SubTitleHeader";
import TemplateCardSkeleton from "@pages/Template/components/TemplateCardSkeleton";
import { useTemplate } from "@utils/useTemplate";
import { templateTypeMapping } from "@pages/Template/utils/constants";
import Loader from "@shared/components/Loader";
import NewItemButton from "@shared/components/NewItemButton";

const TEMPLATES_PER_PAGE = 9;

const ChooseTemplatePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { reportName, projectId, checkedItems } = location.state || {};

  const [dropdownOpen, setDropdownOpen] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<"Mine" | "All">("Mine");
  const [dropdownFilter, setDropdownFilter] = useState<string>("Latest");
  const [reportTypeDropdownFilter, setReportTypeDropdownFilter] =
    useState<string>("Select Report Type");
  const [templateTypes, setTemplateTypes] = useState<string[]>([]);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const offset = (currentPage - 1) * TEMPLATES_PER_PAGE;

  const [templateSelected, setTemplateSelected] = useState<boolean>(false);

  const { templates, loading, setTemplates, totalCount } = useFetchTemplates(
    activeTab,
    dropdownFilter,
    reportTypeDropdownFilter,
    offset,
    TEMPLATES_PER_PAGE
  );

  const {
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    selectedTemplateId,
    setSelectedTemplateId,
    handleDelete,
  } = useDeleteTemplate();

  const { handleGenerateReport, isGenerating, error } = useGenerateReport(
    projectId,
    reportName,
    checkedItems
  );

  const { fetchTemplatTypes } = useTemplate();

  useEffect(() => {
    (async () => {
      try {
        const types: string[] = await fetchTemplatTypes();
        let formattedOptions = types
          .filter((type) => templateTypeMapping[type])
          .map((type) => templateTypeMapping[type]);
        formattedOptions = ["Select Report Type", ...formattedOptions];
        setTemplateTypes(formattedOptions);
      } catch (error) {
        console.error("Failed to fetch template types", error);
      }
    })();
  }, []);

  // Reset page to 1 on filter or tab change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, dropdownFilter, reportTypeDropdownFilter]);

  const onHandleGenerateReport = async (templateId: number) => {
    try {
      setTemplateSelected(true);
      await handleGenerateReport(templateId);
    } catch (error) {
      console.error("Error generating report:", error);
    } finally {
      setTemplateSelected(false);
    }
  };

  const handleCreateTemplate = () => {
    navigate("/create-template", {
      state: {
        projectId,
        reportName,
        checkedItems,
        isChoosing: true,
        returnUrl: "/choose-template",
      },
    });
  };

  const handleEditClick = (templateId: number) => {
    navigate(`/template/${templateId}/edit`, {
      state: {
        projectId,
        reportName,
        checkedItems,
        isChoosing: true,
        returnUrl: "/choose-template",
      },
    });
  };

  const totalPages = Math.ceil(totalCount / TEMPLATES_PER_PAGE);

  return (
    <div className="px-8 pb-3 min-h-screen flex-col flex flex-1">
      <SubTitleHeader
        title={`Choose a Template`}
        subtitle={`Select a template to generate your report.`}
        subtitleStyle="text-gray-400 text-sm 2xl:text-base"
        childrenTitle={
          <TemplatesTabs
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            dropdownFilter={dropdownFilter}
            setDropdownFilter={setDropdownFilter}
            reportTypeDropdownFilter={reportTypeDropdownFilter}
            setReportTypeDropdownFilter={setReportTypeDropdownFilter}
            templateTypes={templateTypes}
          />
        }
        children={
          <NewItemButton
            onClick={handleCreateTemplate}
            title="Template"
            btnStyle="w-[11.5rem] 2xl:w-[12rem]"
          />
        }
      />

      <div className="mt-6 pt-6 flex flex-col flex-1 relative">
        {templateSelected && (
          <div className="absolute inset-0 bg-[#FFFFFE] opacity-50 z-10 flex items-center justify-center">
            <Loader size="lg" color={"#184CD8"} />
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 flex-1">
            {Array.from({ length: 9 }).map((_, idx) => (
              <TemplateCardSkeleton key={idx} />
            ))}
          </div>
        ) : templates.length === 0 ? (
          <p className="text-gray-500 mt-4">No templates found.</p>
        ) : (
          <div className="flex-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {templates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onEdit={handleEditClick}
                  onDelete={(id) => {
                    setSelectedTemplateId(id);
                    setIsDeleteModalOpen(true);
                  }}
                  onCardClick={onHandleGenerateReport}
                  dropdownOpen={dropdownOpen}
                  setDropdownOpen={setDropdownOpen}
                />
              ))}
            </div>
          </div>
        )}
        {totalPages > 1 && (
          <div className="my-8 flex justify-center space-x-2">
            <button
              className="px-4 py-2 bg-gray-200 text-gray-600 rounded-md disabled:opacity-50"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            >
              Previous
            </button>

            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                className={`px-4 py-2 rounded-md ${
                  currentPage === i + 1
                    ? "bg-blue-500 text-white"
                    : "bg-gray-200 text-gray-600"
                }`}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}

            <button
              className="px-4 py-2 bg-gray-200 text-gray-600 rounded-md disabled:opacity-50"
              disabled={currentPage === totalPages}
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
            >
              Next
            </button>
          </div>
        )}

        {error && <p className="text-red-500 mt-4">{error}</p>}

        <ConfirmDeleteModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={handleDelete}
          entityNames={
            templates.find((t) => t.id === selectedTemplateId)?.name || ""
          }
        />
      </div>
    </div>
  );
};

export default ChooseTemplatePage;
